import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";

type Tenant = { id: string; name: string; slug: string };

type Contact = {
  id: string;
  tenant_id: string;
  email: string;
  verified_at: string | null;
  active: boolean;
};

type RequestRow = {
  id: string;
  tenant_id: string;
  requested_by_hub_user_id: string;
  requested_role: string;
  status: string;
  requested_at: string;
  expires_at: string | null;
  approved_by_email: string | null;
};

export function AdminBreakGlass() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantId, setTenantId] = useState<string>("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [reason, setReason] = useState("");
  const [requestedRole, setRequestedRole] = useState<"ReadOnly" | "Admin">("ReadOnly");
  const [durationSeconds, setDurationSeconds] = useState(3600);

  async function loadTenants() {
    const data = await apiFetch<{ tenants: Tenant[] }>("/api/admin/tenants");
    setTenants(data.tenants);
    if (!tenantId && data.tenants[0]) setTenantId(data.tenants[0].id);
  }

  async function loadRequests() {
    const data = await apiFetch<{ requests: RequestRow[] }>("/api/admin/emergency-access/requests");
    setRequests(data.requests);
  }

  async function loadContacts(id: string) {
    const data = await apiFetch<{ contacts: Contact[] }>(`/api/admin/tenants/${id}/break-glass/contacts`);
    setContacts(data.contacts);
  }

  async function refreshAll(opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      await loadTenants();
      await loadRequests();
      if (tenantId) await loadContacts(tenantId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    void refreshAll();
    let last = 0;
    function maybeRefresh() {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 15_000) return;
      last = now;
      void refreshAll({ silent: true });
    }
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      try {
        await loadContacts(tenantId);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [tenantId]);

  async function requestEmergencyAccess() {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/api/admin/tenants/${tenantId}/emergency-access/requests`, {
        method: "POST",
        body: { reason, requested_role: requestedRole, duration_seconds: durationSeconds }
      });
      setReason("");
      await loadRequests();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <PageLayout
        title="Break-glass"
        description="Request emergency access to a tenant. Requests notify tenant-managed break-glass contacts for approval."
      >
        {error && <ErrorState detail={error} />}
        {loading && <LoadingState />}

        <div className="card" style={{ marginTop: 12 }}>
          <label className="field">
            <span className="label">Tenant</span>
            <select className="select" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Break-glass contacts</div>
          <div className="help" style={{ marginBottom: 12 }}>
            Managed by the tenant (admin). Operators can view the list to confirm who will receive approvals.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {contacts.map((c) => (
              <div key={c.id} style={{ border: "1px solid #f1f5f9", borderRadius: 10, padding: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 700 }}>{c.email}</div>
                  <span className={c.verified_at ? "pill pillOk" : "pill pillNeutral"}>
                    {c.verified_at ? "verified" : "unverified"}
                  </span>
                </div>
              </div>
            ))}
            {contacts.length === 0 && <div className="muted">No contacts yet.</div>}
          </div>
        </div>

        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Request emergency access</div>
          <div className="help" style={{ marginBottom: 12 }}>
            Requests are time-bound and require approval. Use a clear, auditable reason.
          </div>
          <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
            <label className="field">
              <span className="label">Reason</span>
              <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} />
            </label>
            <label className="field">
              <span className="label">Requested role</span>
              <select className="select" value={requestedRole} onChange={(e) => setRequestedRole(e.target.value as any)}>
                <option value="ReadOnly">ReadOnly</option>
                <option value="Admin">Admin</option>
              </select>
            </label>
            <label className="field">
              <span className="label">Duration (seconds)</span>
              <input type="number" className="input" value={durationSeconds} onChange={(e) => setDurationSeconds(Number(e.target.value))} />
            </label>
          </div>
          <div className="actions">
            <button
              onClick={() => void requestEmergencyAccess()}
              disabled={loading || !tenantId || reason.trim() === ""}
              className="btn btnPrimary"
              type="button"
            >
              Request access
            </button>
          </div>
        </div>

        <div className="card tableWrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Role</th>
                <th>Status</th>
                <th>Approved by</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td className="muted mono" style={{ fontSize: 12 }}>
                    {r.tenant_id}
                  </td>
                  <td><span className="pill">{r.requested_role}</span></td>
                  <td><span className="pill pillNeutral">{r.status}</span></td>
                  <td className="muted">{r.approved_by_email ?? "—"}</td>
                  <td className="muted">{r.expires_at ?? "—"}</td>
                </tr>
              ))}
              {requests.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ padding: 12 }}>
                    No requests.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </PageLayout>
    </div>
  );
}

